import type { LinkView } from "../api";

interface SummaryCardsProps {
  links: LinkView[];
  isLoading: boolean;
}

type Tone = "blue" | "green" | "yellow" | "red";

export function SummaryCards({ links, isLoading }: SummaryCardsProps) {
  const total = links.length;
  const online = links.filter((link) => link.enabled && link.lastStatus === "online" && (link.lastFailedCount ?? 0) === 0).length;
  const issues = links.filter(
    (link) => link.enabled && (link.lastStatus === "offline" || link.lastStatus === "degraded" || (link.lastFailedCount ?? 0) > 0)
  ).length;
  const unchecked = links.filter((link) => link.enabled && link.lastStatus === "not_checked").length;

  const cards: Array<{ label: string; value: number; tone: Tone }> = [
    { label: "Total", value: total, tone: "blue" },
    { label: "Online", value: online, tone: "green" },
    { label: "Issues", value: issues, tone: issues > 0 ? "red" : "green" },
    { label: "Unchecked", value: unchecked, tone: "yellow" }
  ];

  return (
    <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Monitoring summary">
      {cards.map((card) => (
        <div key={card.label} className="rounded-lg border border-[#24201b] bg-[#08090b] px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-[#928a82]">
            <span className={`h-2 w-2 rounded-full ${dotClass(card.tone)}`} aria-hidden="true" />
            {card.label}
          </div>
          <div className={`mt-3 text-3xl font-semibold tracking-normal ${numberClass(card.tone)}`}>
            {isLoading ? "-" : card.value}
          </div>
        </div>
      ))}
    </section>
  );
}

function dotClass(tone: Tone): string {
  const classes: Record<Tone, string> = {
    blue: "bg-[#2da8ff]",
    green: "bg-[#40d780]",
    yellow: "bg-[#f4b63f]",
    red: "bg-[#ff5f57]"
  };
  return classes[tone];
}

function numberClass(tone: Tone): string {
  const classes: Record<Tone, string> = {
    blue: "text-[#7ec7ff]",
    green: "text-[#52e391]",
    yellow: "text-[#ffd16a]",
    red: "text-[#ff7b72]"
  };
  return classes[tone];
}
